"use client";

import { useEffect, useState } from "react";
import type { NotificationState } from "../hooks/useNotifications";

const DISMISS_KEY = "push_banner_dismissed";
const DISMISS_DAYS = 30;

interface Props {
  /** Current notification state from useNotifications() */
  state: NotificationState;
  /** Subscribe callback from useNotifications() */
  onSubscribe: () => Promise<boolean>;
  /** Called when the user needs to install the PWA first (iOS) */
  onInstallRequired: () => void;
}

/**
 * In-app banner prompting users to enable push notifications.
 * Shown before the browser's system permission dialog so the user
 * understands what they're opting into.
 *
 * Only appears for users in "prompt" state who haven't dismissed recently.
 * In standalone PWA mode, shows after 3s. In browser, shows after 8s.
 */
export function PushNotificationBanner({ state, onSubscribe, onInstallRequired }: Props) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    // Only show when permission can be requested
    if (state !== "prompt" && state !== "ios-needs-install") return;

    // Check dismiss cooldown
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // Show sooner in standalone mode (user has already installed)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as unknown as Record<string, unknown>).standalone === true;

    const delay = isStandalone ? 3000 : 8000;
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [state]);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setAnimating(true);
    setTimeout(() => setVisible(false), 200);
  }

  async function handleEnable() {
    if (state === "ios-needs-install") {
      onInstallRequired();
      dismiss();
      return;
    }

    const success = await onSubscribe();
    if (success) {
      setAnimating(true);
      setTimeout(() => setVisible(false), 200);
    }
    // If denied, the banner disappears naturally since state changes to "denied"
  }

  if (!visible) return null;

  return (
    <div
      style={{
        ...bannerStyle,
        opacity: animating ? 0 : 1,
        transform: animating ? "translateY(10px)" : "translateY(0)",
        transition: "opacity 0.2s, transform 0.2s",
      }}
    >
      {/* Bell icon */}
      <div style={iconContainerStyle}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "#6cf" }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: 2, fontSize: "0.85rem" }}>
          Enable notifications
        </div>
        <div style={{ color: "var(--color-text-faintest)", fontSize: "0.78rem" }}>
          Get notified when you receive new messages
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
        <button onClick={handleEnable} style={enableButtonStyle}>
          Enable
        </button>
        <button onClick={dismiss} aria-label="Dismiss" style={dismissButtonStyle}>
          &times;
        </button>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const bannerStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 28, // above the footer
  left: 12,
  right: 12,
  zIndex: 1000,
  background: "var(--color-toast-bg)",
  color: "var(--color-toast-text)",
  borderRadius: 12,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  lineHeight: 1.4,
};

const iconContainerStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  background: "rgba(102, 204, 255, 0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const enableButtonStyle: React.CSSProperties = {
  background: "#6cf",
  color: "#000",
  border: "none",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: "0.8rem",
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const dismissButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--color-text-faint)",
  fontSize: "1.2rem",
  cursor: "pointer",
  padding: "4px 4px",
  lineHeight: 1,
  flexShrink: 0,
};
